// src/components/auth/TenantLookup.tsx
// ✅ REDESIGN 2025-11-01: qmLEO branding update
// ✅ UPDATE 2025-01-29: Removed real department key from placeholder, added help tooltip
// CHANGES: qmLEO logo, removed generic shield, consistent branding

import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Loader2, ArrowRight, HelpCircle, X } from 'lucide-react';
import { tenantManagementAPI } from '../../services/tenantManagementAPI';
import qmLeoLogo from '../../assets/images/qmLEO_logo.png';

interface TenantInfo {
  tenantId: string;
  organizationName: string;
  tenantSlug: string;
}

interface TenantLookupProps {
  onTenantSelected: (tenant: TenantInfo) => void;
}

const TenantLookup: React.FC<TenantLookupProps> = ({ onTenantSelected }) => {
  const [departmentKey, setDepartmentKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  // Load saved tenant slug on mount
  useEffect(() => {
    const savedSlug = localStorage.getItem('qm_lastTenantSlug');
    if (savedSlug) {
      setDepartmentKey(savedSlug);
    }
  }, []);

  // Close help tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setShowHelp(false);
      }
    };

    if (showHelp) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelp]);

  const handleContinue = async () => {
    if (!departmentKey.trim()) {
      setError('Please enter your department key');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Looking up department:', departmentKey);

      const lookupResponse = await tenantManagementAPI.getTenantBySlug(departmentKey.trim().toLowerCase());

      if (!lookupResponse.success || !lookupResponse.data) {
        const errorMessage = lookupResponse.error?.message || 'Department not found. Please check your department key.';
        console.error('❌ Department lookup failed:', errorMessage);
        setError(errorMessage);
        setLoading(false);
        return;
      }

      const tenantInfo: TenantInfo = {
        tenantId: lookupResponse.data.tenantId,
        organizationName: lookupResponse.data.tenant.organizationName,
        tenantSlug: departmentKey.trim().toLowerCase()
      };

      console.log('✅ Department found:', tenantInfo);

      // Store tenant info and last used slug
      localStorage.setItem('qm_selectedTenant', JSON.stringify(tenantInfo));
      localStorage.setItem('qm_lastTenantSlug', tenantInfo.tenantSlug);
      
      // Store tenant features for feature gating (e.g., Asset Tracking module)
      const tenantFeatures = lookupResponse.data.tenant.features || [];
      localStorage.setItem('qm_tenantFeatures', JSON.stringify(tenantFeatures));
      console.log('📦 Tenant features stored:', tenantFeatures);

      // Proceed to login screen
      onTenantSelected(tenantInfo);

    } catch (err: any) {
      console.error('❌ Department lookup error:', err);
      setError(err.message || 'Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && departmentKey.trim()) {
      handleContinue();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header with qmLEO Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src={qmLeoLogo} 
              alt="qmLEO" 
              className="h-24 w-auto"
            />
          </div>
          <p className="text-gray-600">
            Law Enforcement Inventory Management
          </p>
        </div>

        {/* Department Selection Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Select Your Department
            </h2>
            <p className="text-sm text-gray-600">
              Enter your department's unique access key to continue
            </p>
          </div>

          <div className="space-y-4">
            {/* Department Key Input */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Department Key <span className="text-red-500">*</span>
                </label>
                {/* Help Icon with Tooltip */}
                <div className="relative" ref={helpRef}>
                  <button
                    type="button"
                    onClick={() => setShowHelp(!showHelp)}
                    className="text-blue-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-full"
                    aria-label="What is a Department Key?"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                  
                  {/* Help Tooltip */}
                  {showHelp && (
                    <div className="absolute left-0 top-6 z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">Department Key</h4>
                        <button
                          type="button"
                          onClick={() => setShowHelp(false)}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="Close help"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600">
                        Your Department Key is a unique identifier for your organization's qmLEO site. 
                        If you're unsure of your Department Key, contact your qmLEO administrator for assistance.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <input
                type="text"
                value={departmentKey}
                onChange={(e) => {
                  setDepartmentKey(e.target.value.toLowerCase());
                  if (error) setError(null);
                }}
                onKeyPress={handleKeyPress}
                placeholder="Enter department key"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors lowercase"
                disabled={loading}
                autoFocus
                autoComplete="off"
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Continue Button */}
            <button
              onClick={handleContinue}
              disabled={loading || !departmentKey.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 py-3 px-4 rounded-lg text-white font-medium transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Verifying department...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need access? Contact your department administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantLookup;
